// Help Articles API functions

import type { UserAuth } from '@repo/common';
import type { HelpArticle, HelpCategory } from '@repo/common/types/Blog';
import constants from '@repo/sharedlib/src/utils/constants';
import { getPrivateClient } from './common.data';

/**
 * Fetch all help categories from Sanity
 */
export const getHelpCategories = async (
  auth: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<HelpCategory[]> => {
  try {
    const client = getPrivateClient(auth, { timeout: constants.TIMEOUT_MEDIUM });
    const response = await client.get('/help/categories');
    return response.data.categories || [];
  } catch (error) {
    console.error('Error fetching help categories:', error);
    return [];
  }
};

/**
 * Fetch all help articles from Sanity
 */
export const getHelpArticles = async (
  auth: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<HelpArticle[]> => {
  try {
    const client = getPrivateClient(auth, { timeout: constants.TIMEOUT_MEDIUM });
    const response = await client.get('/help/articles');
    return response.data.articles || [];
  } catch (error) {
    console.error('Error fetching help articles:', error);
    return [];
  }
};

/**
 * Fetch a specific help article by slug
 * Note: The API returns markdown text, so we parse it into a HelpArticle object
 */
export const getHelpArticle = async (
  slug: string,
  auth: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<HelpArticle | null> => {
  try {
    const client = getPrivateClient(auth, { timeout: constants.TIMEOUT_MEDIUM });
    const response = await client.get(`/help/articles/${slug}`, {
      headers: {
        Accept: 'text/markdown'
      }
    });

    // The response is markdown text
    if (response.data && typeof response.data === 'string') {
      // Extract title from the first line (assumes "# Title" format)
      const lines = response.data.split('\n');
      const titleLine = lines[0];
      const title = titleLine.startsWith('# ') ? titleLine.substring(2).trim() : 'Help Article';

      return {
        _type: 'helpArticle',
        title,
        slug: { current: slug, _type: 'slug' },
        contentPlainText: response.data
      };
    }
    return null;
  } catch (error: any) {
    // 404 means article not found
    if (error?.response?.status === 404) {
      return null;
    }
    console.error(`Error fetching help article ${slug}:`, error);
    return null;
  }
};
